import store from "@/store";
import Axios from "axios";
import xhrAdapter from "axios/lib/adapters/xhr";
import inFlightManager from "@/api/inFlightManagerSingleton.js";

// ===== REFRESH QUEUE STATE (module scope so every request shares it) =====
let isRefreshing = false;
let refreshPromise = null;
let refreshSubscribers = [];
function subscribeTokenRefresh(cb) { refreshSubscribers.push(cb); }
function onTokenRefreshed(accessToken) {
  refreshSubscribers.forEach((cb) => cb(accessToken));
  refreshSubscribers = [];
}

// ===== TEST METRICS (dev-friendly; harmless in prod) =====
const __TEST_METRICS__ = {
  refreshCount: 0,                // how many times refreshToken() ran
  networkCountByKey: new Map(),   // how many times the real adapter fired per de-dup key
  increment(key) { this.networkCountByKey.set(key, (this.networkCountByKey.get(key) || 0) + 1); },
  get(key) { return this.networkCountByKey.get(key) || 0; },
  reset() { this.refreshCount = 0; this.networkCountByKey.clear(); },
};

export default (useCustomSerializer) => {
  const client = Axios.create({
    baseURL: "/",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });

  client.defaults.timeout = 30000;

  if (useCustomSerializer) {
    client.defaults.paramsSerializer = {
      serialize: (params) => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value === null || value === undefined) {
            return;
          }

          // Handle arrays of objects (like Sort)
          if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
            value.forEach((item, index) => {
              Object.entries(item).forEach(([subKey, subValue]) => {
                if (subValue !== null && subValue !== undefined) {
                  searchParams.append(`${key}[${index}].${subKey}`, subValue);
                }
              });
            });
          }
          // Handle simple arrays
          else if (Array.isArray(value)) {
            value.forEach(item => {
              searchParams.append(`${key}`, item);
            });
          } else if (value instanceof Date) {
            searchParams.append(key, value.toISOString());
          }
          // Handle simple values
          else {
            searchParams.append(key, value);
          }
        });

        return searchParams.toString();
      }
    }
  }
  else {
    client.defaults.paramsSerializer = {
      indexes: null
    };
  }

  const realAdapter = xhrAdapter;

  // ===== Request interceptor =====
  client.interceptors.request.use(
    (config) => {
      const token = store.state.token?.access_token ?? null;
      const connectionId = store.state.NotificationStore?.connectionId ?? null;

      if (token) config.headers["Authorization"] = "Bearer " + token;
      if (connectionId) config.headers["connectionId"] = connectionId;

      // Deduplicate GET requests
      if (config.method.toLowerCase() === 'get') {
        const key = generateCacheKey(config);
        config.adapter = async (adapterConfig) => {
          if (inFlightManager.has(key)) {
            console.debug(`Deduplicating GET request: ${key}`);
            return inFlightManager.get(key); // return existing in-flight promise (no network increment)
          }

          // Count a *real* network hit only when we actually kick off the adapter
          __TEST_METRICS__.increment(key);

          const requestPromise = realAdapter(adapterConfig)
            .then((response) => {
              inFlightManager.delete(key);
              return response;
            })
            .catch((error) => {
              inFlightManager.delete(key);
              throw error;
            });

          inFlightManager.set(key, requestPromise);
          return requestPromise;
        };
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // ===== Response interceptor =====
  client.interceptors.response.use(
    (response) => {
      if (response.status === 200 || response.status === 201) {
        const meta = response.config.meta;
        if (meta?.successText) showSuccessSnackbar(response);
        return response;
      }
      return Promise.reject(response);
    },
    async (error) => {
      if (error?.code === "ERR_CANCELED") {
        return Promise.reject(error);
      }

      const originalRequest = error?.config;

      // If the refresh endpoint itself 401s/fails -> logout
      if (originalRequest?.url?.includes("/api2/token/refresh")) {
        store.dispatch("logout");
        return Promise.reject(error.response ?? error);
      }

      if (error?.response?.status === 401) {
        if (originalRequest?._retry) {
          // Already retried once -> give up and logout
          store.dispatch("logout");
          return Promise.reject(error.response ?? error);
        }

        originalRequest._retry = true;

        // If refresh is already underway, queue this request to run after it completes
        if (isRefreshing && refreshPromise) {
          return new Promise((resolve, reject) => {
            subscribeTokenRefresh((newAccessToken) => {
              try {
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers["Authorization"] = "Bearer " + newAccessToken;
                resolve(client(originalRequest));
              } catch (e) {
                reject(e);
              }
            });
          });
        }

        // Start a single shared refresh
        isRefreshing = true;
        refreshPromise = (async () => {
          try {
            const data = await refreshToken();
            await store.dispatch("updateToken", data); // await in case it persists to storage
            onTokenRefreshed(data.access_token);
            return data;
          } catch (refreshError) {
            // wake any waiters by clearing queue, then logout
            refreshSubscribers = [];
            store.dispatch("logout");
            throw refreshError;
          } finally {
            isRefreshing = false;
            refreshPromise = null;
          }
        })();

        const data = await refreshPromise;
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers["Authorization"] = "Bearer " + data.access_token;
        return client(originalRequest);
      }

      if (error?.response?.config?.meta) {
        showErrorSnackbar(error);
      }

      return Promise.reject(error.response ?? error);
    }
  );

  // expose metrics for tests
  client.__TEST_METRICS__ = __TEST_METRICS__;

  return client;
};

// ===== Helpers =====

function generateCacheKey(config) {
  const url = new URL(config.url, 'http://dummy');
  if (config.params) {
    const searchParams = new URLSearchParams(config.params);
    url.search = searchParams.toString();
  }
  return `${config.method.toUpperCase()}:${url.pathname}?${url.search}`;
}

function showSuccessSnackbar(response) {
  let snackbarmethod;
  switch (response.config.method) {
    case "put":
      snackbarmethod = "simpleCreateSuccess";
      break;
    case "post":
    case "patch":
      snackbarmethod = "simpleChangeSuccess";
      break;
    case "delete":
      snackbarmethod = "simpleDeleteSuccess";
      break;
    default:
      snackbarmethod = "responseSnackbar";
  }
  store.dispatch(
    "triggerevents/" + snackbarmethod,
    response.config.meta?.successText
  );
}

function showErrorSnackbar(error) {
  const meta = error.response?.config?.meta;
  store.dispatch("triggerevents/responseSnackbar", {
    response: error.response,
    method: meta?.clientApiMethod,
    text: meta?.onErrorMessage
      ? meta.onErrorMessage(error)
      : error.response?.data?.StatusCode !== 500
        ? error.response?.data?.Message
        : "Unexpected error. Contact support.",
    input: meta?.input,
    color:
      meta?.color ??
      (error.response?.data?.StatusCode !== 500 ? "warning" : null),
    icon:
      meta?.icon ??
      (error.response?.data?.StatusCode !== 500
        ? "mdi-information-outline"
        : null),
    noClutter: meta?.noClutter ?? false,
  });
}

async function refreshToken() {
  __TEST_METRICS__.refreshCount++; // count each refresh run (for tests)
  const token = store.state.token?.refresh_token ?? null;
  const refreshTokenClient = Axios.create({
    baseURL: "/",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
  });
  const response = await refreshTokenClient.post("/api2/token/refresh");
  return response.data;
}